interface AppConfiguration {
  auth: {
    clientId: string;
    tenantId: string;
    authority: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    scopes: string[];
  };
  api: {
    baseUrl: string;
  };
  app: {
    name: string;
    environment: string;
  };
}

class ConfigService {
  private static instance: ConfigService;
  private config: AppConfiguration | null = null;
  private configPromise: Promise<AppConfiguration> | null = null;

  private constructor() {}

  static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  async getConfig(): Promise<AppConfiguration> {
    if (this.config) {
      return this.config;
    }

    if (this.configPromise) {
      return this.configPromise;
    }

    this.configPromise = this.fetchConfig();
    this.config = await this.configPromise;
    return this.config;
  }

  private async fetchConfig(): Promise<AppConfiguration> {
    // Determine the API base URL
    // In development, we might be running on different ports
    // In production, config endpoint will be on the same domain under /api path
    const apiBaseUrl = this.determineApiBaseUrl();
    
    const configUrl = `${apiBaseUrl}/api/config`;
    
    try {
      console.log('Fetching configuration from:', configUrl);
      
      const response = await fetch(configUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Include origin header to help backend determine redirect URI
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
      }

      const config = await response.json();
      console.log('Configuration fetched successfully:', {
        hasClientId: !!config.auth?.clientId,
        hasTenantId: !!config.auth?.tenantId,
        apiBaseUrl: config.api?.baseUrl,
        appName: config.app?.name,
        environment: config.app?.environment,
        scopes: config.auth?.scopes
      });
      console.log('Full config received:', config);

      return config;
    } catch (error) {
      console.error('Failed to fetch configuration:', error);
      // Provide fallback configuration for local development
      return this.getFallbackConfig();
    }
  }

  private determineApiBaseUrl(): string {
    // Check if we're in development mode with a different API port
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      // Development mode - API is typically on a different port
      return 'http://localhost:6001';
    }

    // Production mode - API is on the same domain under /api path
    return window.location.origin;
  }

  private getFallbackConfig(): AppConfiguration {
    console.warn('Using fallback configuration - this should only happen in development');
    
    return {
      auth: {
        clientId: 'fallback-client-id',
        tenantId: 'fallback-tenant-id',
        authority: 'https://login.microsoftonline.com/fallback-tenant-id',
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
        scopes: ['User.Read', 'openid', 'profile', 'email']
      },
      api: {
        baseUrl: this.determineApiBaseUrl()
      },
      app: {
        name: 'Auth App',
        environment: 'development'
      }
    };
  }

  // Helper methods for easy access to specific config parts
  getAuthConfig() {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call getConfig() first.');
    }
    return this.config.auth;
  }

  getApiConfig() {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call getConfig() first.');
    }
    return this.config.api;
  }

  getAppConfig() {
    if (!this.config) {
      throw new Error('Configuration not loaded. Call getConfig() first.');
    }
    return this.config.app;
  }

  // Clear cached config (useful for testing or config refresh)
  clearCache() {
    this.config = null;
    this.configPromise = null;
  }
}

// Export singleton instance
export const configService = ConfigService.getInstance();
export type { AppConfiguration };